import React, { useMemo, useState } from "react";
import apiFetch from "../../utils/apiFetch";
import T4PreviewTopBar from "../../components/payroll/T4PreviewTopBar";

import { getToken } from "./helpers";
import { useT4Preview } from "./useT4Preview";
import Slip from "./components/Slip";
import ReversePage from "./components/ReversePage";
import type { T4Employee } from "./types";

const TopBar = T4PreviewTopBar as any;

interface SlipsBlock {
  type: "slips";
  pair: T4Employee[];
  key: string;
}

interface ReverseBlock {
  type: "reverse";
  key: string;
}

type Block = SlipsBlock | ReverseBlock;

export default function T4EmployeeSlips() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYear);

  const { data, isLoading: loading, error: queryError } = useT4Preview(year);

  const employer = data ? data.employer : null;
  const employees: T4Employee[] =
    data && Array.isArray(data.employees) ? data.employees : [];
  const errMsg = queryError instanceof Error ? queryError.message : null;
  const error = errMsg ? "Could not load T4 employee slips: " + errMsg : "";

  const blocks = useMemo<Block[]>(() => {
    const out: Block[] = [];
    for (let i = 0; i < employees.length; i += 2) {
      out.push({
        type: "slips",
        pair: employees.slice(i, i + 2),
        key: "s" + i,
      });
      out.push({ type: "reverse", key: "r" + i });
    }
    return out;
  }, [employees]);

  const handleDownload = async () => {
    const token = getToken();
    try {
      const res = await apiFetch(
        "/api/v1/payroll/taxes/t4-employee-slips.pdf?year=" + year,
        { headers: { Authorization: "Bearer " + token } }
      );
      if (!res.ok) {
        alert("Could not generate PDF");
        return;
      }
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (e) {
      alert(
        "Could not generate PDF: " +
          (e instanceof Error ? e.message : String(e))
      );
    }
  };

  return (
    <div
      style={{
        background: "#EDEFF2",
        fontFamily: "Arial, Helvetica, sans-serif",
        color: "#000",
        width: "100%",
        minHeight: "100vh",
      }}
    >
      <style>{`
        .t4-page { background:#fff; width:880px; max-width:100%; min-height:1120px; margin:12px auto 24px; padding:30px 32px; box-shadow:0 1px 6px rgba(16,30,40,.16); font-size:9px; }
        @media print {
          @page { size: letter; margin: 0.35in; }

          html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }

          .t4-noprint { display: none !important; }

          body > div, body > div > div { background: #fff !important; }

          .t4-page {
            background: #fff !important;
            box-shadow: none !important;
            border: none !important;
            margin: 0 !important;
            padding: 0 !important;
            width: auto !important;
            max-width: 100% !important;
            min-height: 0 !important;
            height: auto !important;
            page-break-inside: avoid !important;
            page-break-after: always !important;
            break-after: page !important;
          }
          .t4-page:last-child {
            page-break-after: auto !important;
            break-after: auto !important;
          }

          .t4-cutline:after { display: none !important; }
        }
        .t4-cutline { border-top:1px dashed #666; margin:20px 0; position:relative; }
        .t4-cutline:after { content:"\\2702"; position:absolute; left:6px; top:-10px; font-size:14px; background:#fff; padding:0 5px; color:#555; }
        .t4-rcut { border-top:1px dashed #666; margin:14px 0; position:relative; }
        .t4-rcut:after { content:"\\2702"; position:absolute; left:6px; top:-9px; font-size:12px; background:#fff; padding:0 4px; color:#555; }
      `}</style>

      <TopBar
        title="T4 employee slips"
        subtitle={
          employees.length === 0
            ? "No employee data for " + year
            : "Employee copies · cut along the dashed line · " +
              employees.length +
              " employee" +
              (employees.length === 1 ? "" : "s")
        }
        year={year}
        onYearChange={setYear}
        loading={loading}
        error={!!error}
        downloadDisabled={loading || employees.length === 0}
        onDownload={handleDownload}
      />

      {loading && (
        <div
          className="t4-page"
          style={{
            textAlign: "center",
            padding: 60,
            fontSize: 14,
            fontFamily: "Inter, system-ui, sans-serif",
          }}
        >
          Loading T4 data for {year}...
        </div>
      )}

      {error && !loading && (
        <div
          className="t4-page"
          style={{ padding: 40, fontFamily: "Inter, system-ui, sans-serif" }}
        >
          <div
            style={{
              background: "#FEF2F2",
              border: "1px solid #FCA5A5",
              borderRadius: 8,
              padding: "12px 16px",
              color: "#991B1B",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {error}
          </div>
        </div>
      )}

      {!loading && !error && employees.length === 0 && (
        <div
          className="t4-page"
          style={{
            textAlign: "center",
            padding: 60,
            fontSize: 14,
            fontFamily: "Inter, system-ui, sans-serif",
          }}
        >
          No paycheques have been issued for {year} yet. Complete a payroll run before previewing T4 slips.
        </div>
      )}

      {!loading && !error && employer &&
        blocks.map((b) =>
          b.type === "reverse" ? (
            <ReversePage key={b.key} />
          ) : (
            <div key={b.key} className="t4-page">
              {b.pair.map((emp, j) => (
                <React.Fragment key={j}>
                  <Slip employer={employer} employee={emp} year={year} />
                  <div className="t4-cutline" />
                </React.Fragment>
              ))}
            </div>
          )
        )}
    </div>
  );
}